import React, { FC, useState } from 'react';
import { Box, Container, Divider, Grid, makeStyles, Tab, Tabs, Theme, Typography, useMediaQuery } from '@material-ui/core';
import { ContentSlider } from 'components/structures/projects';
import { TabPanelProps } from 'components/structures/projects/ContentSlider';
import ModalDialog from '../../components/structures/ModalDialog';
import ProjectModalContent, { ProjectModalContentProps } from 'components/structures/projects/ProjectModalContent';
import { ArrowDownIcon } from '../../svgs/projects';

interface CaseStudiesProps {
  listTabs: Array<{ label: string; icon: any; indexTab: any }>;
  listTabsPanel: Array<TabPanelProps>;
}

function a11yProps(index1: any) {
  return {
    id: `vertical-tab-${index1}`,
    'aria-controls': `vertical-tabpanel-${index1}`,
  };
}

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    color: theme.palette.primary.dark,
    fontWeight: 500,
  },
  orangeDivider: {
    backgroundColor: theme.palette.primary.main,
    maxWidth: 100,
    minHeight: 1,
    margin: theme.spacing(4, 'auto', 6),
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: theme.palette.secondary.light,
    marginBottom: theme.spacing(7),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(12),
    },
  },
  text: {
    fontSize: 22,
    padding: theme.spacing(0, 3),
  },
  container: {
    borderBottom: `2px solid ${theme.palette.secondary.light}`,
    margin: theme.spacing(5, 0),
    '& .MuiTabs-root': {
      marginBottom: -2,
    },
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(7, 0),
    },
  },
  tabs: {
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.primary.main,
      bottom: 0,
    },
  },
  labelsContainer: {
    width: 'auto',
    padding: 0,
  },
  iconLabelWrapper: {
    flexDirection: 'row',
    marginRight: 10,
    '& svg': {
      transform: 'scale(0.65)',
      '& path': {
        fill: theme.palette.secondary.dark,
      },
    },
  },
  labelIcon: {
    color: theme.palette.secondary.dark,
    fontSize: 18,
    fontWeight: 500,
    maxWidth: 350,
    '&:focus': {
      color: theme.palette.primary.main,
      '& span': {
        '& svg': {
          transform: 'scale(0.65)',
          '& path': {
            fill: theme.palette.primary.main,
          },
        },
      },
    },
    '& .MuiTouchRipple-root': {
      color: 'transparent',
    },
  },
  boxItem: {
    padding: theme.spacing(2, 2.5),
    borderBottom: '1px solid #C8C8C8',
  },
  mobIcon: {
    '& svg': {
      transform: 'scale(0.65)',
      '& path': {
        fill: '#9D9D9D',
      },
    },
  },
  arrowIcon: {
    '& svg': {
      '& path': {
        fill: '#9D9D9D',
      },
    },
  },
  activeIcon: {
    '& svg': {
      '& path': {
        fill: theme.palette.primary.main,
      },
    },
  },
  label: {
    fontWeight: 500,
    fontSize: 20,
    color: '#9D9D9D',
  },
  active: {
    fontWeight: 500,
    fontSize: 20,
    color: theme.palette.primary.main,
  },
}));

const CaseStudies: FC<CaseStudiesProps> = ({ listTabs, listTabsPanel }) => {
  const classes = useStyles();
  const [value, setValue] = React.useState(0);
  const [readMore, setReadMore] = React.useState<ProjectModalContentProps | null>(null);
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
  const [activeIndex, setActiveIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const onReadMore = (item: ProjectModalContentProps) => {
    setReadMore(item);
  };

  const onCloseDialog = () => {
    setReadMore(null);
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    const tabHash = listTabs[newValue]?.label.toLowerCase().replace(/\s+/g, '');
    window.location.hash = `#${tabHash}`;
  };

  const mobileHandleChange = (label: string, index: number) => {
    if (isExpanded) {
      setIsExpanded(false);
      setActiveIndex(index);
      const tabHash = label.toLowerCase().replace(/\s+/g, '');
      window.location.hash = `#${tabHash}`;
    } else {
      setIsExpanded(true);
    }
  };

  return (
    <Box mt={10} id={'case-studies'}>
      <Box className={classes.divider} />
      <Typography variant="h2" align="center" className={classes.title} data-aos="fade-up">
        Completed Projects
      </Typography>
      <Divider className={classes.orangeDivider} />
      <Typography align="center" color="textSecondary" className={classes.text} data-aos="fade-up" data-aos-delay="200">
        We are committed to showing you proof for every project we do!
      </Typography>
      {isSmall ? (
        <Box display="flex" justifyContent="center" className={classes.container} data-aos="fade-up" data-aos-delay="300">
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="scrollable auto tabs example"
            className={`${classes.tabs} `}
          >
            {listTabs.map(({ label, icon, indexTab }, index) => (
              <Tab
                label={label}
                icon={icon}
                classes={{
                  wrapper: classes.iconLabelWrapper,
                  labelIcon: classes.labelIcon,
                }}
                key={index}
                {...a11yProps(indexTab)}
              />
            ))}
          </Tabs>
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" mt={8} mb={2.5} data-aos="fade-up" data-aos-delay="300">
          <Grid container direction="column">
            {listTabs.map(({ label, icon }, index) => (
              <Grid
                onClick={() => mobileHandleChange(label, index)}
                item
                key={label}
                className={`${!isExpanded && activeIndex !== index ? 'hidden' : ''}`}
                style={{
                  opacity: isExpanded || activeIndex === index ? 1 : 0,
                  maxHeight: isExpanded || activeIndex === index ? '80px' : '0',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  className={`${classes.boxItem}`}
                  borderTop={index === 0 || (!isExpanded && activeIndex === index) ? '1px solid #C8C8C8' : undefined}
                >
                  <Box display="flex" alignItems="center">
                    {icon && (
                      <Box className={`${classes.mobIcon} ${activeIndex === index ? classes.activeIcon : ''}`}>{icon}</Box>
                    )}
                    <Box className={`${classes.label} ${activeIndex === index ? classes.active : ''}`}>{label}</Box>
                  </Box>
                  {activeIndex === index && (
                    <Box className={`${classes.arrowIcon} ${activeIndex === index ? classes.activeIcon : ''}`}>
                      <ArrowDownIcon />
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      <Container fixed data-aos="fade-up" data-aos-delay="400">
        {listTabsPanel.map((item, index) => (
          <ContentSlider
            value={isSmall ? value : activeIndex}
            indexTabPanel={item.indexTabPanel}
            key={index}
            title={item.title}
            description={item.description}
            href={item.href}
            image={item.image}
            address={item.address}
            addressName={item.addressName}
            addressIcon={item.addressIcon}
            addressIconMobile={item.addressIconMobile}
            reverse={index % 2 === 1}
            onReadMore={onReadMore}
          />
        ))}
      </Container>
      <ModalDialog open={!!readMore} onClose={onCloseDialog} maxWidth={1170}>
        {!!readMore && <ProjectModalContent {...readMore} />}
      </ModalDialog>
    </Box>
  );
};

export default CaseStudies;
